/**
 * @file ConversationFeed.tsx
 * @description Лента диалога (chat-window-design.md §3, §5, §6): пузыри,
 * разделители дней, линия «Новые сообщения», служебные записи (полностью или
 * свёрнутыми группами), пилюля «↓ Новые сообщения · N» при прокрутке вверх.
 *
 * Прокрутка: при открытии — к линии «Новые сообщения» или в конец; новое
 * сообщение у нижнего края — в конец, иначе ленту не дёргаем, а считаем в
 * пилюлю. Переход из «Истории диалога» — прокрутка к записи и подсветка 2 с.
 * Лента — `role="log"`, `aria-live="polite"`.
 *
 * Ранние сообщения (cabinet-api-contract §12, «чат-2»): сервер отдаёт
 * последние; есть ранние (`hasEarlier`) — вверху кнопка «Показать ранние
 * сообщения». Дочитанная страница встаёт над лентой, а видимое место не
 * прыгает: прокрутка сдвигается на высоту добавленного.
 *
 * Лента в `memo`: ввод в поле ответа и поиск в списке её не перерисовывают
 * (пропсы стабильны — `useMessageSender` отдаёт постоянные `onRetry` /
 * `onRemove`).
 */

import { ArrowDown } from "lucide-react";
import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { Button } from "../ui/button";
import type { ChatFeedItem } from "../../lib/api-chat";
import { CHAT_TEXT } from "./chat-texts";
import { buildFeedRows } from "./feed-model";
import { FeedRowView, FeedSkeleton } from "./FeedRowView";
import type { PendingMessage } from "./useMessageSender";

const NEAR_BOTTOM_PX = 80;
const FLASH_MS = 2_000;

interface Props {
  items: ChatFeedItem[];
  /** Есть сообщения раньше показанных (сервер: `hasMore`). */
  hasEarlier?: boolean;
  loadingEarlier?: boolean;
  onLoadEarlier?: () => void;
  pending: PendingMessage[];
  loading: boolean;
  firstUnreadId: string | null;
  serviceShown: boolean;
  highlightEventId: string | null;
  /** Запись, к которой прокрутить (из «Истории диалога»); `nonce` — повтор того же. */
  focusEvent: { id: string; nonce: number } | null;
  wide: boolean;
  onRetry: (tempId: string) => void;
  onRemove: (tempId: string) => void;
}

export const ConversationFeed = memo(function ConversationFeed(props: Props) {
  const { items, pending, loading, firstUnreadId, serviceShown, highlightEventId, focusEvent, wide } = props;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<ReadonlySet<string>>(new Set());
  const [newBelow, setNewBelow] = useState(0);
  const [flashId, setFlashId] = useState<string | null>(null);
  const placed = useRef(false);
  const count = items.length + pending.length;
  const prevCount = useRef(count);
  /** Высота и прокрутка до дочитки ранних — чтобы видимое место не прыгнуло. */
  const earlierAnchor = useRef<{ height: number; top: number } | null>(null);
  const prepended = useRef(false);

  const rows = useMemo(
    () => buildFeedRows(items, pending, { firstUnreadId, serviceShown, expandedGroups, highlightEventId }),
    [items, pending, firstUnreadId, serviceShown, expandedGroups, highlightEventId],
  );

  const toBottom = (smooth: boolean) => {
    const el = scrollRef.current;
    // В jsdom у элемента нет scrollTo — ставим позицию напрямую.
    if (el && typeof el.scrollTo === "function") el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
    else if (el) el.scrollTop = el.scrollHeight;
    setNewBelow(0);
  };

  // Первое размещение: к линии «Новые сообщения» или в конец.
  useLayoutEffect(() => {
    if (loading || placed.current || items.length === 0) return;
    placed.current = true;
    const line = scrollRef.current?.querySelector<HTMLElement>("[data-chat-unread-line]");
    if (line && typeof line.scrollIntoView === "function") line.scrollIntoView({ block: "start" });
    else toBottom(false);
  }, [loading, items.length]);

  // Дочитаны ранние (сменилось начало ленты): держим видимое место —
  // сдвиг прокрутки на высоту добавленного; в пилюлю «новые» они не идут.
  const firstAt = items[0]?.at;
  const prevFirstAt = useRef(firstAt);
  useLayoutEffect(() => {
    const changed = firstAt !== prevFirstAt.current;
    prevFirstAt.current = firstAt;
    const anchor = earlierAnchor.current;
    if (!changed || !anchor) return;
    earlierAnchor.current = null;
    prepended.current = true;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight - anchor.height + anchor.top;
  }, [firstAt]);

  // Новые элементы внизу: у нижнего края — в конец, иначе — в пилюлю.
  useEffect(() => {
    const added = count - prevCount.current;
    prevCount.current = count;
    if (prepended.current) {
      prepended.current = false;
      return;
    }
    if (added <= 0 || !placed.current) return;
    const el = scrollRef.current;
    const nearBottom = !el || el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
    const lastPending = pending.length > 0 && pending[pending.length - 1]?.status === "sending";
    if (nearBottom || lastPending) toBottom(true);
    else setNewBelow((n) => n + added);
  }, [count, pending]);

  // Переход из «Истории диалога»: раскрыть, прокрутить, подсветить.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  useEffect(() => {
    if (!focusEvent) return;
    setFlashId(focusEvent.id);
    const group = rowsRef.current.find((r) => r.type === "eventGroup" && r.events.some((e) => e.id === focusEvent.id));
    if (group) setExpandedGroups((s) => new Set([...s, group.key]));
    // Прокрутка — после отрисовки раскрытой группы.
    const scroll = window.setTimeout(() => {
      const el = scrollRef.current?.querySelector<HTMLElement>(`[data-chat-event="${focusEvent.id}"]`);
      if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 60);
    const timer = window.setTimeout(() => setFlashId(null), FLASH_MS);
    return () => {
      window.clearTimeout(scroll);
      window.clearTimeout(timer);
    };
  }, [focusEvent]);

  if (loading) return <FeedSkeleton />;

  const loadEarlier = () => {
    const el = scrollRef.current;
    earlierAnchor.current = el ? { height: el.scrollHeight, top: el.scrollTop } : null;
    props.onLoadEarlier?.();
  };
  const ctx = {
    wide,
    flashId,
    onRetry: props.onRetry,
    onRemove: props.onRemove,
    onExpandGroup: (key: string) => setExpandedGroups((s) => new Set([...s, key])),
  };

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={scrollRef}
        role="log"
        aria-live="polite"
        aria-label={CHAT_TEXT.feedLabel}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3 md:px-4"
        onScroll={(e) => {
          const el = e.currentTarget;
          if (el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX) setNewBelow(0);
        }}
      >
        {count === 0 ? <p className="mx-auto max-w-[46ch] py-8 text-center text-sm text-on-surface-variant">{CHAT_TEXT.emptyConversation}</p> : null}
        {props.hasEarlier ? (
          <div data-chat-earlier="" className="mb-2 flex justify-center">
            <Button variant="ghost" size="sm" className="min-h-12 lg:min-h-8" loading={props.loadingEarlier} onClick={loadEarlier}>
              {CHAT_TEXT.showEarlier}
            </Button>
          </div>
        ) : null}
        <div className={wide ? "mx-auto flex max-w-[720px] flex-col gap-2" : "flex flex-col gap-2"}>
          {rows.map((row) => (
            <FeedRowView key={row.key} row={row} ctx={ctx} />
          ))}
        </div>
      </div>
      {newBelow > 0 ? (
        <Button variant="soft" size="sm" className="absolute bottom-3 left-1/2 min-h-10 -translate-x-1/2 rounded-full shadow-sm" onClick={() => toBottom(true)}>
          <ArrowDown className="size-4" aria-hidden />
          {CHAT_TEXT.newMessagesPill(newBelow)}
        </Button>
      ) : null}
    </div>
  );
});
